import { makeAutoObservable, runInAction } from 'mobx';

export const FEED_CHATS_STORAGE_KEY = 'littlebird-feed-composer-chats';
export const FEED_CHAT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
export const FEED_CHAT_MAX_ENTRIES = 20;

export type FeedChatEntry = {
  threadId: string;
  startedAt: number;
  seen: boolean;
  completedAt?: number;
};

type FeedChatStorage = Pick<Storage, 'getItem' | 'setItem'>;

const parseEntries = (value: string | null): FeedChatEntry[] => {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is FeedChatEntry =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof entry.threadId === 'string' &&
        entry.threadId.length > 0 &&
        typeof entry.startedAt === 'number' &&
        Number.isFinite(entry.startedAt) &&
        typeof entry.seen === 'boolean'
    );
  } catch {
    return [];
  }
};

export class FeedChatsStore {
  entries: FeedChatEntry[];

  constructor(
    private readonly storage: FeedChatStorage = window.localStorage,
    private readonly now: () => number = Date.now
  ) {
    this.entries = this.withinLimits(
      parseEntries(storage.getItem(FEED_CHATS_STORAGE_KEY)),
      now()
    );
    makeAutoObservable<FeedChatsStore, 'storage' | 'now'>(
      this,
      { storage: false, now: false },
      { autoBind: true }
    );
    this.persist();
  }

  add(threadId: string): void {
    const id = threadId.trim();
    if (!id) return;

    runInAction(() => {
      const startedAt = this.now();
      this.entries = this.withinLimits(
        [
          { threadId: id, startedAt, seen: false },
          ...this.entries.filter((entry) => entry.threadId !== id),
        ],
        startedAt
      );
      this.persist();
    });
  }

  setCompletedAt(threadId: string, completedAt: number | undefined): void {
    const entry = this.entries.find(
      (candidate) => candidate.threadId === threadId
    );
    if (!entry || entry.completedAt === completedAt) return;
    entry.completedAt = completedAt;
    this.persist();
  }

  markSeen(threadId: string): void {
    const entry = this.entries.find(
      (candidate) => candidate.threadId === threadId
    );
    if (!entry || entry.seen) return;

    runInAction(() => {
      this.entries = this.entries.map((candidate) =>
        candidate.threadId === threadId
          ? { ...candidate, seen: true }
          : candidate
      );
      this.persist();
    });
  }

  dismiss(threadId: string): void {
    if (!this.entries.some((entry) => entry.threadId === threadId)) return;

    runInAction(() => {
      this.entries = this.entries.filter(
        (entry) => entry.threadId !== threadId
      );
      this.persist();
    });
  }

  private withinLimits(entries: FeedChatEntry[], now: number): FeedChatEntry[] {
    return entries
      .filter((entry) => now - entry.startedAt < FEED_CHAT_MAX_AGE_MS)
      .sort((left, right) => right.startedAt - left.startedAt)
      .slice(0, FEED_CHAT_MAX_ENTRIES);
  }

  private persist(): void {
    try {
      this.storage.setItem(
        FEED_CHATS_STORAGE_KEY,
        JSON.stringify(this.entries)
      );
    } catch {
      // Keep the in-memory list usable when storage is unavailable.
    }
  }
}

export const feedChatsStore = new FeedChatsStore();
